import * as React from "react";
import { ApprovalRequestDialog, type ApprovalAnfrage } from "./ApprovalRequestDialog";

/**
 * Schloss ohne Schreibrecht (Genehmigungen, 30.09.2026): Der Klick auf das
 * Schloss öffnet diesen Dialog. Er sagt, WER zuständig ist, zeigt den Stand
 * der eigenen Freigabe-Anfrage und bietet — sofern keine läuft — Begründung
 * und „Um Genehmigung bitten" an. Eine Erweiterung von
 * `ApprovalRequestDialog`, kein zweiter Dialog.
 */

/** `schreibSperre` aus `GET /contact/:id` (nur ohne Schreibrecht gesetzt). */
export interface SchreibSperre {
    foNamen: string[];
    boName: string | null;
    anfrage: ApprovalAnfrage | null;
}

const datum = (iso: string | null | undefined): string => {
    if (!iso) return "";
    const d = new Date(iso);
    return Number.isNaN(d.getTime())
        ? ""
        : d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });
};

/** „01.10.2026, 14:05" — für „abgelehnt von …, <Zeit>". */
const datumZeit = (iso: string | null | undefined): string => {
    if (!iso) return "";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    return `${datum(iso)}, ${d.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })}`;
};

/** „(von Anna Admin, 01.10.2026, 14:05)" — leer, wenn beides fehlt. */
const vonWann = (anfrage: ApprovalAnfrage): string => {
    const teile = [String(anfrage.decidedByName ?? "").trim() ? `von ${String(anfrage.decidedByName).trim()}` : "", datumZeit(anfrage.decidedAt)].filter(Boolean);
    return teile.length ? ` (${teile.join(", ")})` : "";
};

/** „Du hast keine Rechte für diesen Kontakt, weil FO … und BO … zuständig sind." */
export function keineRechteGrund(sperre: Pick<SchreibSperre, "foNamen" | "boName"> | null | undefined): string {
    const fo = (sperre?.foNamen ?? []).map((n) => String(n ?? "").trim()).filter(Boolean);
    const bo = String(sperre?.boName ?? "").trim();
    const foTeil = fo.length ? `FO ${fo.join(", ")}` : "";
    const boTeil = bo ? `BO ${bo}` : "";
    if (foTeil && boTeil) return `Du hast keine Rechte für diesen Kontakt, weil ${foTeil} und ${boTeil} zuständig sind.`;
    if (foTeil) {
        return `Du hast keine Rechte für diesen Kontakt, weil ${foTeil} zuständig ${fo.length > 1 ? "sind" : "ist"}.`;
    }
    if (boTeil) return `Du hast keine Rechte für diesen Kontakt, weil ${boTeil} zuständig ist.`;
    return "Du hast keine Rechte für diesen Kontakt.";
}

/** Stand der eigenen Anfrage als Satz — oder `null`, wenn es keine gibt. */
export function anfrageStatusText(anfrage: ApprovalAnfrage | null | undefined): string | null {
    if (!anfrage) return null;
    switch (anfrage.status) {
        case "PENDING":
            return `Anfrage läuft seit ${datum(anfrage.createdAt)}.`;
        case "DENIED":
            return `Deine Anfrage wurde abgelehnt${vonWann(anfrage)}.`;
        case "REVOKED":
            return `Deine Freigabe wurde entzogen${vonWann(anfrage)}.`;
        case "GRANTED":
            return anfrage.abgelaufen
                ? `Freigabe abgelaufen am ${datum(anfrage.validUntil)}. Du kannst erneut anfragen.`
                : `Freigabe erteilt${vonWann(anfrage)}.`;
        default:
            return null;
    }
}

/**
 * Nach Ablehnung/Entzug ist „erneut anfragen" möglich, aber nicht mehr der
 * Standard: Der Dialog zeigt zuerst das Ergebnis, das Formular erst auf Klick.
 */
export function erneutAnfragenZugeklappt(anfrage: ApprovalAnfrage | null | undefined): boolean {
    return anfrage?.status === "DENIED" || anfrage?.status === "REVOKED";
}

/** Darf eine neue Anfrage gestellt werden? Nicht, solange eine läuft oder gilt. */
export function neueAnfrageMoeglich(anfrage: ApprovalAnfrage | null | undefined): boolean {
    if (!anfrage) return true;
    if (anfrage.status === "PENDING") return false;
    if (anfrage.status === "GRANTED" && !anfrage.abgelaufen) return false;
    return true;
}

export interface KeineRechteDialogProps {
    open: boolean;
    contactName: string;
    schreibSperre: SchreibSperre | null | undefined;
    busy?: boolean;
    error?: string | null;
    onSubmit: (begruendung: string) => void;
    onCancel: () => void;
}

export const KeineRechteDialog: React.FC<KeineRechteDialogProps> = ({
    open,
    contactName,
    schreibSperre,
    busy,
    error,
    onSubmit,
    onCancel,
}) => {
    const anfrage = schreibSperre?.anfrage ?? null;
    return (
        <ApprovalRequestDialog
            open={open}
            contactName={contactName}
            busy={busy}
            error={error}
            onSubmit={onSubmit}
            onCancel={onCancel}
            titel="Keine Rechte für diesen Kontakt"
            einleitung={
                <>
                    <p style={{ margin: "0 0 8px" }}>{keineRechteGrund(schreibSperre)}</p>
                    <p style={{ margin: 0 }}>
                        Du kannst um Genehmigung bitten: Die Anfrage geht an die Zuständigen
                        (FO/BO) und die Administration; bleibt sie offen, wird die
                        Geschäftsführung gefragt. Wird sie genehmigt, darfst du{" "}
                        <strong>{contactName}</strong> sofort bearbeiten.
                    </p>
                </>
            }
            statusText={anfrageStatusText(anfrage)}
            sendenMoeglich={neueAnfrageMoeglich(anfrage)}
            sendenZugeklappt={erneutAnfragenZugeklappt(anfrage)}
            statusTon={erneutAnfragenZugeklappt(anfrage) ? "abgelehnt" : "neutral"}
        />
    );
};

export default KeineRechteDialog;
